import { Box, Container, Typography, Button, Grid } from "@mui/material";
import { Link } from "react-router-dom";
import { useDocumentTitle } from "../../utils/data";
import { keyframes } from "@mui/system";

const floatBall = keyframes`
  0%, 100% { transform: translate(0, 0); }
  25% { transform: translate(20px, -20px); }
  50% { transform: translate(-10px, 20px); }
  75% { transform: translate(10px, -10px); }
`;

const scaleInCenter = keyframes`
  from { opacity: 0; transform: scale(0.85); }
  to { opacity: 1; transform: scale(1); }
`;

const serviceOfferingsData = [
  {
    icon: "🛒",
    title: "Seamless Shopping Experience",
    description: "Provide your customers with an intuitive, easy-to-navigate app that enhances their shopping journey.",
  },
  {
    icon: "🎯",
    title: "Personalized Recommendations",
    description: "Leverage advanced algorithms to offer tailored product suggestions, boosting customer engagement and sales.",
  },
  {
    icon: "🔐",
    title: "Secure Payment Integration",
    description: "Ensure safe and smooth transactions with robust, secure payment gateway options for your customers.",
  },
  {
    icon: "📦",
    title: "Efficient Inventory Management",
    description: "Keep track of stock, manage listings, and streamline your product catalog for greater operational efficiency.",
  },
  {
    icon: "📈",
    title: "Scalable & Flexible",
    description: "Easily scale your business with a flexible platform that adapts to your growing e-commerce needs.",
  },
  {
    icon: "💬",
    title: "Enhanced Customer Engagement",
    description: "Build strong, lasting relationships with your customers through personalized notifications and offers.",
  },
];

const EcommerceApp = () => {
  useDocumentTitle("Briozon - E-Commerce App");

  return (
    <Box>
      {/* Hero Banner */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #f8fafc 0%, #f0f4ff 100%)",
          py: { xs: 6, sm: 8, md: 10 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Floating decorative elements */}
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            right: "5%",
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(91,124,250,0.1) 0%, rgba(118,75,162,0.1) 100%)",
            animation: `${floatBall} 8s ease-in-out infinite`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "15%",
            left: "3%",
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(118,75,162,0.1) 0%, rgba(91,124,250,0.1) 100%)",
            animation: `${floatBall} 6s ease-in-out infinite 1s`,
          }}
        />

        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, sm: 6, md: 8 }} alignItems="center">
            {/* Left Content */}
            <Grid item xs={12} md={6}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    fontSize: { xs: "12px", sm: "14px" },
                    fontWeight: 700,
                    color: "#5b7cfa",
                    letterSpacing: "2px",
                    mb: 1,
                    display: "block",
                  }}
                >
                  PRODUCT
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: "32px", sm: "42px", md: "48px" },
                    fontWeight: 800,
                    color: "#1b1f33",
                    lineHeight: 1.2,
                    mb: 2,
                  }}
                >
                  E-Commerce{" "}
                  <Box
                    component="span"
                    sx={{
                      background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    App
                  </Box>
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: "16px", sm: "19px", md: "20px" },
                    color: "#4c5472",
                    lineHeight: 1.8,
                    mb: 3,
                    textAlign: "justify",
                  }}
                >
                  Our experienced team creates innovative e-commerce solutions, designed to drive
                  success and enhance your business performance with cutting-edge technology.
                </Typography>
                <Button
                  component={Link}
                  to="/contact"
                  variant="contained"
                  sx={{
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    color: "#fff",
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    borderRadius: "12px",
                    fontSize: "16px",
                    textTransform: "none",
                    boxShadow: "0 8px 24px rgba(91, 124, 250, 0.3)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 32px rgba(91, 124, 250, 0.4)",
                    },
                  }}
                >
                  Get Started
                </Button>
              </Box>
            </Grid>

            {/* Right - Creative Visual */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "relative",
                  height: { xs: 320, sm: 400, md: 450 },
                  animation: `${scaleInCenter} 0.8s ease-out 0.2s both`,
                }}
              >
                {/* Main gradient circle */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 220, sm: 280, md: 320 },
                    height: { xs: 220, sm: 280, md: 320 },
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, rgba(91,124,250,0.15) 0%, rgba(118,75,162,0.15) 100%)",
                    filter: "blur(40px)",
                  }}
                />

                {/* Central icon */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 100, sm: 120 },
                    height: { xs: 100, sm: 120 },
                    borderRadius: "24px",
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: { xs: "48px", sm: "56px" },
                    boxShadow: "0 20px 40px rgba(91, 124, 250, 0.4)",
                    animation: `${floatBall} 6s ease-in-out infinite`,
                    zIndex: 2,
                  }}
                >
                  🛒
                </Box>

                {/* Floating feature cards */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "8%",
                    left: "10%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 7s ease-in-out infinite 0.5s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>💳</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    Payments
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    top: "15%",
                    right: "5%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 8s ease-in-out infinite 1s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>📊</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    Analytics
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: "20%",
                    left: "5%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 9s ease-in-out infinite 1.5s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>📦</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    Inventory
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: "10%",
                    right: "10%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 6s ease-in-out infinite 2s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>🔐</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    Security
                  </Typography>
                </Box>

                {/* Decorative rings */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 200, sm: 260 },
                    height: { xs: 200, sm: 260 },
                    borderRadius: "50%",
                    border: "2px dashed rgba(91, 124, 250, 0.2)",
                    animation: `${floatBall} 10s ease-in-out infinite`,
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 280, sm: 360 },
                    height: { xs: 280, sm: 360 },
                    borderRadius: "50%",
                    border: "1px dashed rgba(118, 75, 162, 0.15)",
                    animation: `${floatBall} 12s ease-in-out infinite reverse`,
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Section 2: AI-Driven Commerce Solution ── */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: "linear-gradient(160deg, #f4f7ff 0%, #ffffff 45%, #f9f5ff 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "radial-gradient(circle, rgba(91,124,250,0.07) 1px, transparent 1px)",
            backgroundSize: "38px 38px",
            pointerEvents: "none",
          }}
        />
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 } }}>
            <Typography
              variant="overline"
              sx={{ color: "#5b7cfa", fontWeight: 700, letterSpacing: "3px", display: "block", mb: 2, fontSize: "13px" }}
            >
              INTELLIGENT SOLUTIONS
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 800, fontSize: { xs: "28px", sm: "36px", md: "44px" }, color: "#1b1f33", mb: 3, lineHeight: 1.2 }}
            >
              AI‑Driven{" "}
              <Box component="span" sx={{ background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Commerce Solution
              </Box>
            </Typography>
            <Typography sx={{ color: "#4c5472", fontSize: { xs: "16px", sm: "18px" }, lineHeight: 1.8, maxWidth: 620, mx: "auto" }}>
              Our E-Commerce app delivers a seamless shopping experience to customers, with a user-friendly interface and secure payment gateway.
            </Typography>
          </Box>

          {/* Bento Row 1 — hero card + 2 side cards */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={5} sx={{ display: "flex", flexDirection: "column" }}>
                <Box
                  sx={{
                    flexGrow: 1,
                    p: { xs: 3, sm: 4 },
                    borderRadius: "24px",
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    position: "relative",
                    overflow: "hidden",
                    minHeight: { xs: 220, md: 260 },
                    boxShadow: "0 16px 56px rgba(91,124,250,0.35)",
                    transition: "all 0.35s ease",
                    "&:hover": { transform: "translateY(-6px)", boxShadow: "0 28px 72px rgba(91,124,250,0.45)" },
                  }}
                >
                  <Box sx={{ position: "absolute", top: "-25%", right: "-10%", width: 200, height: 200, borderRadius: "50%", background: "rgba(255,255,255,0.07)", pointerEvents: "none" }} />
                  <Box sx={{ position: "absolute", bottom: "-15%", left: "-5%", width: 140, height: 140, borderRadius: "50%", background: "rgba(255,255,255,0.05)", pointerEvents: "none" }} />
                  <Box sx={{ fontSize: "42px", mb: 2.5 }}>🎯</Box>
                  <Typography sx={{ fontWeight: 800, fontSize: { xs: "20px", sm: "23px" }, color: "#fff", mb: 1.5, lineHeight: 1.25 }}>
                    Personalized Recommendations
                  </Typography>
                  <Typography sx={{ fontSize: "15px", lineHeight: 1.8, color: "rgba(255,255,255,0.87)" }}>
                    AI-powered suggestions using latest technologies to boost engagement and increase conversion rates across your store.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={7}>
                <Grid container spacing={3}>
                  {[
                    { icon: "🧭", title: "Easy Navigation", desc: "Streamlined checkout flow and intuitive user experience design that keeps customers coming back." },
                    { icon: "📦", title: "Inventory Management", desc: "Robust tools for business owners to manage stock, track listings, and keep operations running smoothly." },
                  ].map((item) => (
                    <Grid item xs={12} sm={6} key={item.title} sx={{ display: "flex", flexDirection: "column" }}>
                      <Box
                        sx={{
                          flexGrow: 1,
                          p: { xs: 3, sm: 3.5 },
                          borderRadius: "20px",
                          background: "#fff",
                          border: "1px solid rgba(91,124,250,0.1)",
                          boxShadow: "0 4px 24px rgba(91,124,250,0.07)",
                          transition: "all 0.3s ease",
                          "&:hover": { transform: "translateY(-6px)", boxShadow: "0 16px 44px rgba(91,124,250,0.14)", border: "1px solid rgba(91,124,250,0.22)" },
                        }}
                      >
                        <Box sx={{ width: 52, height: 52, borderRadius: "14px", background: "linear-gradient(135deg, rgba(91,124,250,0.12) 0%, rgba(118,75,162,0.12) 100%)", border: "1px solid rgba(91,124,250,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "26px", mb: 2 }}>
                          {item.icon}
                        </Box>
                        <Typography sx={{ fontWeight: 700, color: "#1b1f33", fontSize: "17px", mb: 1 }}>{item.title}</Typography>
                        <Typography sx={{ color: "#4c5472", fontSize: "14px", lineHeight: 1.75 }}>{item.desc}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Box>

          {/* Bento Row 2 — 3 equal cards with colored top bars */}
          <Grid container spacing={3}>
            {[
              { icon: "🔐", title: "Secure Payments", desc: "Top-notch security with multiple payment gateway integrations for frictionless, safe transactions.", accent: "#5b7cfa" },
              { icon: "📈", title: "Business Growth", desc: "Grow your online presence, increase revenue consistently, and scale with confidence.", accent: "#9b59b6" },
              { icon: "🚀", title: "Customer Engagement", desc: "Streamline operations and build lasting relationships through personalized notifications and offers.", accent: "#764ba2" },
            ].map((item) => (
              <Grid item xs={12} sm={4} key={item.title} sx={{ display: "flex", flexDirection: "column" }}>
                <Box
                  sx={{
                    flexGrow: 1,
                    p: { xs: 3, sm: 3.5 },
                    borderRadius: "20px",
                    background: "#fff",
                    borderTop: `5px solid ${item.accent}`,
                    border: `1px solid rgba(91,124,250,0.1)`,
                    borderTopColor: item.accent,
                    boxShadow: "0 4px 24px rgba(91,124,250,0.07)",
                    transition: "all 0.3s ease",
                    "&:hover": { transform: "translateY(-6px)", boxShadow: "0 18px 48px rgba(91,124,250,0.14)" },
                  }}
                >
                  <Box sx={{ width: 52, height: 52, borderRadius: "14px", background: `linear-gradient(135deg, ${item.accent} 0%, #764ba2 100%)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "26px", mb: 2, boxShadow: `0 8px 22px rgba(91,124,250,0.25)` }}>
                    {item.icon}
                  </Box>
                  <Typography sx={{ fontWeight: 700, color: "#1b1f33", fontSize: "17px", mb: 1 }}>{item.title}</Typography>
                  <Typography sx={{ color: "#4c5472", fontSize: "14px", lineHeight: 1.75 }}>{item.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Section 3: Retail Applications ── */}
      <Box
        sx={{
          py: { xs: 8, md: 11 },
          background: "linear-gradient(135deg, #eef1ff 0%, #f5f0ff 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box sx={{ position: "absolute", top: "-15%", right: "-8%", width: 350, height: 350, borderRadius: "50%", background: "radial-gradient(circle, rgba(91,124,250,0.1) 0%, transparent 70%)", pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", bottom: "-15%", left: "-8%", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle, rgba(118,75,162,0.1) 0%, transparent 70%)", pointerEvents: "none" }} />
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Grid container spacing={{ xs: 4, md: 7 }} alignItems="center">
            {/* Left — overline + heading + 3 feature rows */}
            <Grid item xs={12} md={6}>
              <Typography variant="overline" sx={{ color: "#5b7cfa", fontWeight: 700, letterSpacing: "2.5px", display: "block", mb: 2, fontSize: "12px" }}>
                RETAIL APPLICATIONS
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: "26px", sm: "32px", md: "38px" }, color: "#1b1f33", mb: 4, lineHeight: 1.2 }}>
                Expertise in{" "}
                <Box component="span" sx={{ background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                  E-Com Integration
                </Box>
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {[
                  { icon: "🔗", title: "Seamless Platform Integration", desc: "Connect with leading e-commerce platforms to extend functionality and reach." },
                  { icon: "🛒", title: "Comprehensive Commerce Platform", desc: "Everything businesses need to engage customers and streamline operations in one place." },
                  { icon: "⚡", title: "Ease of Use & Scalability", desc: "Intuitive store management with scalable features that grow with your business." },
                ].map((item) => (
                  <Box key={item.title} sx={{ display: "flex", alignItems: "flex-start", gap: 2, p: 2.5, borderRadius: "16px", background: "rgba(255,255,255,0.7)", border: "1px solid rgba(91,124,250,0.12)", backdropFilter: "blur(8px)", transition: "all 0.3s ease", "&:hover": { background: "#fff", boxShadow: "0 6px 28px rgba(91,124,250,0.12)", transform: "translateX(5px)" } }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: "12px", background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px", flexShrink: 0, boxShadow: "0 6px 16px rgba(91,124,250,0.28)" }}>
                      {item.icon}
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "15px", color: "#1b1f33", mb: 0.4 }}>{item.title}</Typography>
                      <Typography sx={{ color: "#6b7490", fontSize: "14px", lineHeight: 1.65 }}>{item.desc}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>
            {/* Right — styled text card */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: { xs: 3, sm: 4, md: 5 },
                  borderRadius: "28px",
                  background: "#fff",
                  boxShadow: "0 12px 56px rgba(91,124,250,0.12)",
                  border: "1px solid rgba(91,124,250,0.1)",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Box sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: 5, background: "linear-gradient(90deg, #5b7cfa 0%, #764ba2 100%)" }} />
                <Box sx={{ fontSize: "52px", mb: 3 }}>🏪</Box>
                <Typography sx={{ fontWeight: 700, fontSize: { xs: "20px", sm: "22px" }, color: "#1b1f33", mb: 2 }}>
                  Your Complete Commerce Partner
                </Typography>
                <Typography sx={{ color: "#4c5472", lineHeight: 1.9, fontSize: { xs: "15px", sm: "17px" }, textAlign: "justify", mb: 2.5 }}>
                  Seamlessly integrate with various platforms to enhance your e-commerce app's functionality and provide a smooth shopping experience for your customers.
                </Typography>
                <Typography sx={{ color: "#4c5472", lineHeight: 1.9, fontSize: { xs: "15px", sm: "17px" }, textAlign: "justify" }}>
                  Our E-commerce app provides a comprehensive platform for businesses to engage customers and streamline operations. With a focus on ease of use, secure transactions, and scalable features, it enhances the shopping experience and simplifies store management.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Section 4: Powering Your Business ── */}
      <Box sx={{ py: { xs: 8, md: 11 }, background: "#fff", position: "relative", overflow: "hidden" }}>
        <Box sx={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle, rgba(91,124,250,0.05) 1px, transparent 1px)", backgroundSize: "32px 32px", pointerEvents: "none" }} />
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Grid container spacing={{ xs: 4, md: 7 }} alignItems="center">
            {/* Left — heading + 4 capability chips */}
            <Grid item xs={12} md={6}>
              <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: "26px", sm: "32px", md: "38px" }, color: "#1b1f33", mb: 2, lineHeight: 1.2 }}>
                Powering Your Business with{" "}
                <Box component="span" sx={{ background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                  Advanced E-Commerce
                </Box>
              </Typography>
              <Typography sx={{ color: "#4c5472", lineHeight: 1.9, fontSize: { xs: "15px", sm: "17px" }, mb: 4, textAlign: "justify" }}>
                Leading software development expertise powers our E-commerce app, built with cutting-edge technology to offer a seamless and secure shopping experience. With our skilled professionals driving innovation, your organization is equipped to grow and thrive in a competitive market.
              </Typography>
              <Grid container spacing={2}>
                {[
                  { icon: "🎯", label: "Personalized Recommendations" },
                  { icon: "📦", label: "Efficient Inventory Management" },
                  { icon: "🔐", label: "Secure Payment Integrations" },
                  { icon: "👥", label: "Team of Talented Experts" },
                ].map((chip) => (
                  <Grid item xs={6} key={chip.label}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 2, borderRadius: "14px", background: "linear-gradient(135deg, rgba(91,124,250,0.06) 0%, rgba(118,75,162,0.06) 100%)", border: "1px solid rgba(91,124,250,0.12)" }}>
                      <Box sx={{ fontSize: "22px", flexShrink: 0 }}>{chip.icon}</Box>
                      <Typography sx={{ fontWeight: 600, fontSize: "13px", color: "#1b1f33", lineHeight: 1.4 }}>{chip.label}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
            {/* Right — decorative visual panel */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: { xs: 3, sm: 4 },
                  borderRadius: "28px",
                  background: "linear-gradient(135deg, #5b7cfa 0%, #9b59b6 50%, #764ba2 100%)",
                  boxShadow: "0 20px 72px rgba(91,124,250,0.35)",
                  position: "relative",
                  overflow: "hidden",
                  minHeight: { xs: 280, md: 360 },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                }}
              >
                <Box sx={{ position: "absolute", top: "-20%", right: "-10%", width: 240, height: 240, borderRadius: "50%", background: "rgba(255,255,255,0.08)", pointerEvents: "none" }} />
                <Box sx={{ position: "absolute", top: "15%", left: "-5%", width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.05)", pointerEvents: "none" }} />
                <Box sx={{ position: "absolute", top: "30%", left: "50%", transform: "translateX(-50%)", fontSize: "80px", opacity: 0.9 }}>⚡</Box>
                <Box sx={{ position: "relative", zIndex: 1 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: { xs: "22px", sm: "26px" }, color: "#fff", mb: 1 }}>Built for Growth</Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: "15px", lineHeight: 1.7 }}>
                    Backed by a team of talented experts providing personalized recommendations, inventory management, and secure payment integrations.
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Section 5: Elevate Your Store ── */}
      <Box
        sx={{
          py: { xs: 8, md: 11 },
          background: "linear-gradient(160deg, #f9f5ff 0%, #ffffff 50%, #f4f7ff 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box sx={{ position: "absolute", bottom: "-10%", right: "-5%", width: 320, height: 320, borderRadius: "50%", background: "radial-gradient(circle, rgba(118,75,162,0.08) 0%, transparent 70%)", pointerEvents: "none" }} />
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Grid container spacing={{ xs: 4, md: 7 }} alignItems="center">
            {/* Left — heading + text */}
            <Grid item xs={12} md={5}>
              <Typography variant="overline" sx={{ color: "#764ba2", fontWeight: 700, letterSpacing: "2.5px", display: "block", mb: 2, fontSize: "12px" }}>
                INNOVATION FIRST
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: "26px", sm: "32px", md: "38px" }, color: "#1b1f33", mb: 3, lineHeight: 1.2 }}>
                Elevate Your Store with{" "}
                <Box component="span" sx={{ background: "linear-gradient(135deg, #764ba2 0%, #5b7cfa 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                  Advanced Technology
                </Box>
              </Typography>
              <Typography sx={{ color: "#4c5472", lineHeight: 1.9, fontSize: { xs: "15px", sm: "17px" }, textAlign: "justify" }}>
                With a focus on innovation, we help you transform your e-commerce experience. Our team of experts is dedicated to delivering solutions that enhance customer satisfaction and business growth.
              </Typography>
            </Grid>
            {/* Right — 4 checklist items */}
            <Grid item xs={12} md={7}>
              <Grid container spacing={2.5}>
                {[
                  { icon: "💡", title: "Innovation-Driven", desc: "We stay at the forefront of technology to keep your store ahead of the competition." },
                  { icon: "🏗️", title: "Inventory & Operations", desc: "Our platform supports end-to-end inventory management for seamless daily operations." },
                  { icon: "🔒", title: "Secure Payment Gateways", desc: "Multiple gateway integrations ensure safe, reliable transactions for every customer." },
                  { icon: "🌟", title: "Customer Satisfaction", desc: "Every feature is crafted to delight your customers and drive meaningful business growth." },
                ].map((item) => (
                  <Grid item xs={12} sm={6} key={item.title} sx={{ display: "flex", flexDirection: "column" }}>
                    <Box
                      sx={{
                        flexGrow: 1,
                        p: { xs: 2.5, sm: 3 },
                        borderRadius: "18px",
                        background: "#fff",
                        border: "1px solid rgba(118,75,162,0.1)",
                        boxShadow: "0 4px 20px rgba(118,75,162,0.07)",
                        transition: "all 0.3s ease",
                        "&:hover": { transform: "translateY(-5px)", boxShadow: "0 14px 40px rgba(118,75,162,0.14)", border: "1px solid rgba(118,75,162,0.2)" },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                        <Box sx={{ width: 40, height: 40, borderRadius: "11px", background: "linear-gradient(135deg, #764ba2 0%, #5b7cfa 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px", flexShrink: 0, boxShadow: "0 6px 16px rgba(118,75,162,0.28)" }}>
                          {item.icon}
                        </Box>
                        <Typography sx={{ fontWeight: 700, fontSize: "15px", color: "#1b1f33" }}>{item.title}</Typography>
                      </Box>
                      <Typography sx={{ color: "#6b7490", fontSize: "14px", lineHeight: 1.7 }}>{item.desc}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Section 6: Service Offerings ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, background: "#fff", position: "relative", overflow: "hidden" }}>
        <Box sx={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle, rgba(91,124,250,0.055) 1px, transparent 1px)", backgroundSize: "36px 36px", pointerEvents: "none" }} />
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 } }}>
            <Typography variant="overline" sx={{ color: "#5b7cfa", fontWeight: 700, letterSpacing: "3px", display: "block", mb: 2, fontSize: "13px" }}>
              OUR SERVICE OFFERINGS
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: "28px", sm: "36px", md: "44px" }, color: "#1b1f33", mb: 2, lineHeight: 1.2 }}>
              Foster Meaningful{" "}
              <Box component="span" sx={{ background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Customer Connections
              </Box>
            </Typography>
          </Box>

          {/* Numbered offering cards */}
          <Grid container spacing={3}>
            {serviceOfferingsData.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: "flex", flexDirection: "column" }}>
                <Box
                  sx={{
                    flexGrow: 1,
                    p: { xs: 3, sm: 4 },
                    borderRadius: "22px",
                    background: "linear-gradient(160deg, #f8faff 0%, #fff 100%)",
                    border: "1px solid rgba(91,124,250,0.1)",
                    borderLeft: "5px solid",
                    borderLeftColor: index % 2 === 0 ? "#5b7cfa" : "#764ba2",
                    boxShadow: "0 4px 28px rgba(91,124,250,0.07)",
                    position: "relative",
                    overflow: "hidden",
                    transition: "all 0.35s ease",
                    "&:hover": {
                      transform: "translateY(-7px)",
                      boxShadow: "0 20px 56px rgba(91,124,250,0.15)",
                      background: "#fff",
                    },
                  }}
                >
                  {/* Background number */}
                  <Typography
                    sx={{
                      position: "absolute",
                      top: -8,
                      right: 16,
                      fontSize: "72px",
                      fontWeight: 900,
                      color: index % 2 === 0 ? "rgba(91,124,250,0.07)" : "rgba(118,75,162,0.07)",
                      lineHeight: 1,
                      userSelect: "none",
                      letterSpacing: "-3px",
                    }}
                  >
                    {String(index + 1).padStart(2, "0")}
                  </Typography>
                  <Box
                    sx={{
                      width: 58,
                      height: 58,
                      borderRadius: "16px",
                      background: index % 2 === 0
                        ? "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)"
                        : "linear-gradient(135deg, #764ba2 0%, #5b7cfa 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "28px",
                      mb: 2.5,
                      boxShadow: `0 10px 28px rgba(91,124,250,0.28)`,
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography sx={{ fontWeight: 700, color: "#1b1f33", fontSize: { xs: "17px", sm: "18px" }, mb: 1.5, lineHeight: 1.3 }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ color: "#4c5472", fontSize: "14px", lineHeight: 1.8 }}>
                    {item.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "#fff",
                mb: 2,
                fontSize: { xs: "22px", sm: "28px", md: "32px" },
              }}
            >
              Reach Us Out Here
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "rgba(255,255,255,0.9)",
                mb: 4,
                maxWidth: 650,
                mx: "auto",
                lineHeight: 1.8,
                fontSize: { xs: "16px", sm: "19px", md: "20px" },
                textAlign: "justify",
              }}
            >
              Thank you for reaching out to us. We value your feedback, inquiries, and
              suggestions. Feel free to get in touch with us using the information below.
            </Typography>
            <Button
              component={Link}
              to="/contact"
              variant="contained"
              sx={{
                backgroundColor: "#fff",
                color: "#5b7cfa",
                px: 4,
                py: 1.5,
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "1rem",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.9)",
                },
              }}
            >
              Contact Us Today
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default EcommerceApp;
